/* eslint-disable no-shadow */
import axios from 'axios';
import { oneData, allData } from './normalizer';
import { Identifiable, CreateCRUDBase } from './types';

export const BASE_URL = `${process.env.REACT_APP_API_BASE}`;
export const BASE_HARDWARE_URL = `${process.env.REACT_APP_HARDWARE_API_BASE}`;

const isPreviewMode = () => {
  return (
    sessionStorage.getItem('mode') === 'preview' ||
    (window.location.href.includes('key=') && window.location.href.includes('id='))
  );
};

export const dHeaders: HeadersInit = new Headers({
  Accept: 'application/json',
  'Content-Type': 'application/json'
});

interface AuthHeader {
  Authorization?: string;
}

export class API<T extends Identifiable, U extends Identifiable, V> implements CreateCRUDBase<T, U, V> {
  public entity: string;

  public constructor(entity: string) {
    this.entity = entity;
  }

  public get url(): string {
    return `${BASE_URL}/kiosk/${this.entity}`;
  }

  // eslint-disable-next-line class-methods-use-this
  public getAuthHeader = (): AuthHeader => {
    const token = localStorage.getItem('token');
    const maintenanceToken = localStorage.getItem('maintenance_token');
    const demoToken = sessionStorage.getItem('token');

    if (isPreviewMode() && demoToken) {
      return { Authorization: `Bearer ${demoToken}` };
    }
    if (maintenanceToken) return { Authorization: `Bearer ${maintenanceToken}` };
    if (token) return { Authorization: `Bearer ${token}` };

    return {};
  };

  private logToLocalServer = async (err: any) => {
    if (err && this.url.includes(BASE_URL)) {
      let errString = '';
      try {
        const formattedErr = {
          url: err.config.url,
          method: err.config.method,
          data: err.config.data,
          name: err.name,
          message: err.message,
          stack: err.stack
        };
        errString = JSON.stringify(formattedErr);
      } catch (e) {
        errString = JSON.stringify(err);
      }

      await axios.post(
        `${BASE_HARDWARE_URL}/log`,
        { message: errString },
        {
          headers: { ...dHeaders, ...this.getAuthHeader() }
        }
      );
    }
  };

  public getAll = async <T>(
    query?: { [K in keyof T]: string },
    headers: HeadersInit = {},
    timeout: number = 0
  ): Promise<T[]> => {
    try {
      return allData(
        await axios.get(this.url, {
          headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
          params: query,
          timeout
        })
      );
    } catch (err) {
      await this.logToLocalServer(err);
      throw err;
    }
  };

  public getOne = async <T>(id: string, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.get(`${this.url}/${id}`, { headers: { ...dHeaders, ...headers, ...this.getAuthHeader() }, timeout })
      );
    } catch (err) {
      await this.logToLocalServer(err);
      throw err;
    }
  };

  public create = async <T, V>(data: V, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.post(`${this.url}`, data, {
          headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      await this.logToLocalServer(err);
      throw err;
    }
  };

  public update = async <T, U extends Identifiable>(
    data: U,
    headers: HeadersInit = {},
    timeout: number = 0
  ): Promise<T> => {
    const { id, ...rest } = data;
    try {
      return oneData(
        await axios.put(`${this.url}/${id}`, rest, {
          headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      await this.logToLocalServer(err);
      throw err;
    }
  };

  public destroy = async <T>(id: string, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      return oneData(
        await axios.delete(`${this.url}/${id}`, {
          headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      await this.logToLocalServer(err);
      throw err;
    }
  };
}

export class HardwareAPI<T extends Identifiable, U extends Identifiable, V> extends API<T, U, V> {
  public get url(): string {
    if (isPreviewMode()) {
      return `${window.location.origin}/mocks/${this.entity}.json`;
    }
    return `${BASE_HARDWARE_URL}/${this.entity}`;
  }

  public create = async <T, V>(data: V, headers: HeadersInit = {}, timeout: number = 0): Promise<T> => {
    try {
      if (isPreviewMode()) {
        const response: any = allData(
          await axios.get(this.url, {
            headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
            timeout
          })
        );
        if (this.url.includes('/sale')) {
          response.message.amount = (data as any).amount;
        }
        return response;
      }
      return oneData(
        await axios.post(`${this.url}`, data, {
          headers: { ...dHeaders, ...headers, ...this.getAuthHeader() },
          timeout
        })
      );
    } catch (err) {
      throw err;
    }
  };

  // eslint-disable-next-line class-methods-use-this
  public getAuthHeader = (): AuthHeader => {
    return {};
  };
}

export class KioskAPI<T extends Identifiable, U extends Identifiable, V> extends API<T, U, V> {
  public get url(): string {
    return this.entity;
  }

  // eslint-disable-next-line class-methods-use-this
  public getAuthHeader = (): AuthHeader => {
    return {};
  };
}
